import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetRestaurantTables,
  CreateRestaurantTable,
  UpdateRestaurantTable,
  UpdateRestaurantTableStatus,
  DeleteRestaurantTable } from "../Api";
import { 
  ServiceQueryOptions,
  unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const restaurantTableQueryKeys = {
  restaurantTables: (params?: Parameters<typeof GetRestaurantTables>[0]) =>
    ["restaurantTables", params] as const,
};

export const invalidateRestaurantTableQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["restaurantTables"] });
};

export const useRestaurantTablesQuery = (
  params?: Parameters<typeof GetRestaurantTables>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: restaurantTableQueryKeys.restaurantTables(params),
    queryFn: async () => unwrapApiResponse(await GetRestaurantTables(params)),
  });
};

export const useRestaurantTablesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: Parameters<typeof GetRestaurantTables>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: restaurantTableQueryKeys.restaurantTables(params),
        queryFn: async () =>
          unwrapApiResponse(await GetRestaurantTables(params)),
      }),
  });
};

export const useCreateRestaurantTableMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateRestaurantTable>[0]) =>
      CreateRestaurantTable(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRestaurantTableQueries(queryClient);
      }
    },
  });
};

export const useUpdateRestaurantTableMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateRestaurantTable>[0]) =>
      UpdateRestaurantTable(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRestaurantTableQueries(queryClient);
      }
    },
  });
};

export const useUpdateRestaurantTableStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateRestaurantTableStatus>[0]) =>
      UpdateRestaurantTableStatus(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRestaurantTableQueries(queryClient);
      }
    },
  });
};

export const useDeleteRestaurantTableMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteRestaurantTable>[0]) =>
      DeleteRestaurantTable(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRestaurantTableQueries(queryClient);
      }
    },
  });
};

export const useRestaurantTableService = (config?: {
  fetchRestaurantTablesParams?: Parameters<typeof GetRestaurantTables>[0];
  fetchRestaurantTablesEnabled?: boolean;
}) => {
  const restaurantTablesQuery = useRestaurantTablesQuery(
    config?.fetchRestaurantTablesParams,
    {
      enabled: config?.fetchRestaurantTablesEnabled !== false,
    },
  );

  const createRestaurantTableMutation = useCreateRestaurantTableMutation();
  const updateRestaurantTableMutation = useUpdateRestaurantTableMutation();
  const updateRestaurantTableStatusMutation =
    useUpdateRestaurantTableStatusMutation();
  const deleteRestaurantTableMutation = useDeleteRestaurantTableMutation();

  return {
    restaurantTablesQuery,
    createRestaurantTableMutation,
    updateRestaurantTableMutation,
    updateRestaurantTableStatusMutation,
    deleteRestaurantTableMutation,
  };
};
